/**
 * The SignalR push `conversationCountsChanged` (GLOBAL_CONVERSATIONS_SLICE2_SPEC.md §4.6, §7.4): a conversation write
 * may have changed this user's counts (the nav badge, a project chip or tab dot, an open overlay's tabs).
 *
 * A trigger, never a payload (SD2-09, D48): it carries ids only, no counts, no text and no names, because a recipient
 * may have lost access since. Consumers refetch (ConversationCountsStore, ConversationsFeedStore) and re-enter
 * Angular's zone themselves: UserSignalrService forwards the push outside it.
 *
 * The server sends it to the `user-isolation-{userId}` groups of the write's audience only, never to the whole
 * workspace (Q18, SD2-10). UserSignalrService forwards a push only when `acceptConversationSignal` accepts it.
 *
 * NSwag generates nothing for SignalR payloads (no endpoint returns them), so the C# contract
 * (`ConversationCountsChangedSignalDto`, `ConversationCountsChangeCause`) is mirrored here by hand, as
 * `ClientUpdateSignal` is in user-signalr.service.ts. The only import is a type, erased on compile, so the spec runs
 * under plain jasmine.
 */
import type { ConversationEntryKind } from '@shared/service-proxies/service-proxies';

/** The hub method name. Must match `ConversationConsts.CountsChangedEvent` on the server. */
export const CONVERSATION_COUNTS_CHANGED_EVENT = 'conversationCountsChanged';

/** Why the counts may have changed. Mirrors `ConversationCountsChangeCause` (byte) on the server, value for value. */
export const CONV_CHANGE_CAUSE = {
    /** A task comment or a project update was posted, a root or a reply. */
    messagePosted: 1,
    /** A message's text was edited. Its writer always receives it; a user the edit newly mentions does too. */
    messageEdited: 2,
    /** A note was resolved. */
    noteResolved: 3,
    /** Read state changed: Mark read or unread, Mark all read, the ambient auto-read, Undo, or a legacy read. */
    readStateChanged: 4,
} as const;

/** One of the `CONV_CHANGE_CAUSE` values. */
export type ConvChangeCause = typeof CONV_CHANGE_CAUSE[keyof typeof CONV_CHANGE_CAUSE];

/**
 * The push's argument: the camelCase mirror of `ConversationCountsChangedSignalDto`, exactly its nine members. Every
 * member is always sent; the server writes null where it has no value.
 */
export interface ConversationCountsChangedSignal {
    /** The workspace the write happened in. */
    tenantId: number;
    /** Why the counts may have changed: a `CONV_CHANGE_CAUSE` value. */
    cause: ConvChangeCause;
    /** The kind of the written entry's root when known (1 task comment, 2 project update, 3 email); else null. */
    entryKind: ConversationEntryKind | null;
    /**
     * The written entry's root (a reply names its root; a resolve or a per-comment read names that comment); null
     * when not known (Mark all read, a legacy task read).
     */
    entryId: number | null;
    /** The project of a project update (kind 2 only); null otherwise. */
    projectId: number | null;
    /** The task of a task comment or a task read (kind 1 only); null otherwise. */
    taskId: number | null;
    /** The user whose write this was. */
    actorUserId: number;
    /**
     * The tab whose request made the write, set only when that request's answer already carried the counts
     * (SetReadState, AutoReadAmbient, Undo); null for every other write, so the acting tab refetches too (SD2-10).
     */
    originTabId: string | null;
    /** When the server sent it, UTC, as the ISO string the hub delivers (not parsed here). */
    asOfUtc: string;
}

/**
 * Whether this tab forwards a `conversationCountsChanged` push to its consumers. Drops:
 *  - no payload;
 *  - a session with no workspace (a host user) or a push for another workspace (a second guard: the server sends to
 *    this user's own group only);
 *  - this tab's own echo: `originTabId` names this tab (SD2-10 echo rule).
 * A push with no `originTabId` is accepted even when this tab made the write: the server names the tab only when the
 * write's answer already carried the counts, and for every other write the acting tab refetches to reconcile its
 * optimistic change (D48).
 *
 * @param payload the push's argument as the hub delivered it.
 * @param tenantId this session's workspace (`AppSessionService.tenantId`; null for a host user).
 * @param isFromThisTab whether an origin tab id names this tab: UserSignalrService passes BrowserTabHelper's
 * `isFromThisTab`, a spec passes a stub. Not asked when an earlier rule already dropped the push.
 */
export function acceptConversationSignal(
    payload: ConversationCountsChangedSignal | null | undefined,
    tenantId: number | null | undefined,
    isFromThisTab: (originTabId: string | null | undefined) => boolean
): boolean {
    if (!payload) {
        return false;
    }
    if (!tenantId || payload.tenantId !== tenantId) {
        return false;
    }
    return !isFromThisTab(payload.originTabId);
}
